import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Axios from 'functions/Axios';
import { ButtonC } from 'Common/Buttons';
import {
  GETAllActiveSKUCodes,
  GET_ALL_USERS_BY_CLIENT,
  GET_ASSET_TYPE_FILTER_DATA,
} from '../../DAM_API/urls';
import ValueField, { commonSelectProps } from './ValueField';
import {
  DATA_POINT_OPTIONS,
  JOIN_HINTS,
  JOIN_OPTIONS,
  buildAdvancedFilters,
  createEmptyRow,
  getDataPoint,
  getOperatorOptions,
} from './fields';

const ORIENTATION_OPTIONS = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'square', label: 'Square' },
];

/**
 * `sharedWith` / `updatedBy` are matched on the user id, so that is what the
 * option carries into the payload. Display names repeat across users on the same
 * client, so the email rides along to tell them apart in the menu.
 */
const toUserOptions = (users) =>
  (users || []).map((user) => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    return {
      value: user?.userID,
      label: name || user?.userEmail,
      email: user?.userEmail,
    };
  });

/** Chips stay compact; the menu shows the email so duplicate names are separable. */
const formatUserOption = (option, { context }) =>
  context === 'menu' && option.email ? (
    <div className="advanced-search__user-option">
      <span>{option.label}</span>
      <span className="advanced-search__user-email">{option.email}</span>
    </div>
  ) : (
    option.label
  );

/**
 * Advanced Search panel.
 *
 * Builds the `advancedFilters` array consumed by the existing `/apidam/search`
 * endpoint and hands it back through `onSearch`; the parent screen owns the
 * request so advanced criteria travel alongside the keyword and the sidebar
 * filters that are already in play.
 */
const AdvancedSearch = ({ isOpen, onClose, onSearch, onClear, rows, setRows }) => {
  const [fileTypeData, setFileTypeData] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    const loadOptionData = async () => {
      const [types, skus] = await Promise.allSettled([
        Axios.get(GET_ASSET_TYPE_FILTER_DATA),
        Axios.get(GETAllActiveSKUCodes),
      ]);

      if (cancelled) return;

      if (types.status === 'fulfilled') {
        setFileTypeData(types.value?.data?.data || []);
      }

      if (skus.status === 'fulfilled') {
        setSkuOptions((skus.value?.data?.data || []).map((sku) => ({ value: sku, label: sku })));
      }
    };

    loadOptionData().catch((err) => console.error('Error loading advanced search options:', err));

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // The user list is loaded by the select itself so it is always current, and
  // `searchText` is served by the API rather than filtered in the browser.
  // react-select asks on every keystroke, so a burst of typing costs one call:
  // superseded requests are dropped before they are sent.
  const loadUserOptions = useMemo(() => {
    let timer;

    return (searchText) =>
      new Promise((resolve) => {
        clearTimeout(timer);
        timer = setTimeout(async () => {
          try {
            const { data } = await Axios.get(
              `${GET_ALL_USERS_BY_CLIENT}?searchText=${encodeURIComponent(searchText || '')}`,
            );
            resolve(toUserOptions(data?.data));
          } catch (err) {
            console.error('Error searching users:', err);
            resolve([]);
          }
        }, 350);
      });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const optionSources = useMemo(
    () => ({
      fileTypes: {
        options: fileTypeData.map((item) => ({ value: item?.type, label: item?.type })),
      },
      fileExtensions: {
        options: Array.from(new Set(fileTypeData.flatMap((item) => item?.extensions || []))).map(
          (extension) => ({ value: extension, label: extension }),
        ),
      },
      orientations: { options: ORIENTATION_OPTIONS },
      skuCodes: { options: skuOptions },
      users: {
        loadOptions: loadUserOptions,
        formatOptionLabel: formatUserOption,
      },
    }),
    [fileTypeData, skuOptions, loadUserOptions],
  );

  const updateRow = (id, changes) =>
    setRows((previous) => previous.map((row) => (row.id === id ? { ...row, ...changes } : row)));

  const handleDataPointChange = (row, selected) => {
    const field = selected?.value || '';
    const operators = getOperatorOptions(field);

    // The data point drives both the operator list and the value control, so a
    // change resets whatever the previous data point had collected.
    updateRow(row.id, {
      field,
      operator: operators[0]?.value || '',
      value: null,
    });
  };

  const addRow = () => setRows((previous) => [...previous, createEmptyRow()]);

  const removeRow = (id) =>
    setRows((previous) => {
      const remaining = previous.filter((row) => row.id !== id);
      return remaining.length ? remaining : [createEmptyRow()];
    });

  const filters = buildAdvancedFilters(rows);

  if (!isOpen) return null;

  return (
    <div className="advanced-search__panel no-print">
      <div className="advanced-search__title">Create a custom search criteria</div>

      {rows.map((row, index) => {
        const dataPoint = getDataPoint(row.field);
        const operatorOptions = getOperatorOptions(row.field);

        return (
          <React.Fragment key={row.id}>
            {index > 0 && (
              <div className="advanced-search__join">
                <label className="advanced-search__label">Join</label>
                <Select
                  options={JOIN_OPTIONS}
                  {...commonSelectProps}
                  className="advanced-search__join-select"
                  value={JOIN_OPTIONS.find((option) => option.value === rows[index - 1].join)}
                  onChange={(selected) =>
                    updateRow(rows[index - 1].id, { join: selected?.value || 'AND' })
                  }
                />
                <div className="advanced-search__hint">
                  {JOIN_HINTS[rows[index - 1].join] || ''}
                </div>
              </div>
            )}

            <div className="advanced-search__row">
              <div className="advanced-search__row-head">
                <div className="advanced-search__control advanced-search__control--datapoint">
                  <label className="advanced-search__label">Data Point</label>
                  <Select
                    options={DATA_POINT_OPTIONS}
                    {...commonSelectProps}
                    value={DATA_POINT_OPTIONS.find((option) => option.value === row.field) || null}
                    onChange={(selected) => handleDataPointChange(row, selected)}
                    placeholder="Select data point"
                  />
                </div>

                {rows.length > 1 && (
                  <button
                    type="button"
                    className="advanced-search__remove"
                    onClick={() => removeRow(row.id)}
                  >
                    <span className="advanced-search__remove-icon">&#8722;</span> Remove
                  </button>
                )}
              </div>

              <div className="advanced-search__row-body">
                <div className="advanced-search__control advanced-search__control--narrow">
                  <label className="advanced-search__label">Condition</label>
                  <Select
                    options={operatorOptions}
                    {...commonSelectProps}
                    isDisabled={!row.field}
                    value={operatorOptions.find((option) => option.value === row.operator) || null}
                    onChange={(selected) => updateRow(row.id, { operator: selected?.value || '' })}
                    placeholder="Select condition"
                  />
                </div>

                <div className="advanced-search__control advanced-search__control--wide">
                  <label className="advanced-search__label">Value</label>
                  <ValueField
                    field={row.field}
                    value={row.value}
                    optionSources={optionSources}
                    onChange={(value) => updateRow(row.id, { value })}
                  />
                </div>
              </div>

              {dataPoint?.valueType === 'multi' && (
                <div className="advanced-search__hint advanced-search__hint--indent">
                  Matches an asset when any of the selected values match.
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}

      <button type="button" className="advanced-search__add" onClick={addRow}>
        <span className="advanced-search__add-icon">+</span> Add More
      </button>

      <div className="advanced-search__footer">
        {filters.length > 0 && (
          <button type="button" className="advanced-search__clear" onClick={onClear}>
            Clear All
          </button>
        )}
        <ButtonC text="Close" size="lg" color="Secondary" click={onClose} />
        <ButtonC
          text="Search Now"
          size="lg"
          disabled={filters.length === 0}
          click={() => onSearch(filters)}
        />
      </div>
    </div>
  );
};

export default AdvancedSearch;
